import GeneralDialog from './general-dialog';
import { FieldError, FieldErrors } from 'react-hook-form';
import { useEffect, useRef } from 'react';
import { logClientError } from '../api/users-api';

import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';

interface ZodValidationDialogProps {
  open: boolean;
  handleClose: () => void;
  errors: FieldErrors;
}

type FlatError = {
  path: string;
  error: FieldError;
};

export default function ZodValidationDialog({
  open,
  handleClose,
  errors,
}: ZodValidationDialogProps) {
  const hasLoggedErrors = useRef(false);
  // const [errorList, setErrorList] = useState<string[]>([]);
  const LOGGABLE_ZOD_ERROR_TYPES = new Set(['invalid_type', 'invalid_string']);

  function isLoggableError(error?: FieldError): boolean {
    if (!error) return false;

    // Filter out only invalid type/input error
    return LOGGABLE_ZOD_ERROR_TYPES.has(error.type);
  }

  useEffect(() => {
    if (hasLoggedErrors.current) return;

    const getFlattenedErrors = (errs: FieldErrors, path = ''): FlatError[] => {
      return Object.entries(errs).flatMap(([key, value]) => {
        const currentPath = path ? `${path}.${key}` : key;
        // If it's a leaf node with a message
        if (value && 'message' in value && typeof value.message === 'string') {
          return [{ path: currentPath, error: value as FieldError }];
        }
        // If it's a nested object (e.g., an array or nested group), recurse
        if (value && typeof value === 'object') {
          return getFlattenedErrors(value as FieldErrors, currentPath);
        }
        return [];
      });
    };

    const flatErrors = getFlattenedErrors(errors);

    const loggableErrors = flatErrors.filter(({ error }) =>
      isLoggableError(error)
    );

    if (loggableErrors.length > 0) {
      const summary = `Zod Validation Failed: ${loggableErrors
        .map((e) => `${e.path}: ${e.error.message || ''}`)
        .join(', ')}`;
      logClientError(
        new Error(summary),
        'zod-validation-dialog',
        window.location.href,
        'error'
      );
      hasLoggedErrors.current = true;
    }
  }, [errors]);

  return (
    <GeneralDialog
      open={open}
      handleClose={handleClose}
      title={`Missing Required Information`}
      dialogContent={
        <Box sx={{ px: 4 }}>
          <Typography sx={{ mb: 2 }}>
            Please review the highlighted steps and provide the required missing
            information.
          </Typography>
          {/* TODO: still needs work to modify zod messages for all cases to be more user friendly */}
          {/* <Alert severity="warning">
            <AlertTitle sx={{ fontSize: 14 }}>
              Validation Error{errorList.length > 1 ? 's' : ''}
            </AlertTitle>
            <List dense sx={{ py: 0, listStyleType: 'disc', pl: 2 }}>
              {errorList.map((error) => (
                <ListItem
                  key={error}
                  disableGutters
                  sx={{ display: 'list-item', py: 0 }}
                >
                  <ListItemText
                    primary={error}
                    slotProps={{
                      primary: {
                        sx: {
                          wordBreak: 'break-word',
                          fontSize: 12,
                        },
                      },
                    }}
                  />
                </ListItem>
              ))}
            </List>
          </Alert> */}
        </Box>
      }
    />
  );
}
